import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { getLandingPath } from "./landing";
import { getOnTimeLandingPath } from "../ontime/utils/access";
import { clearActiveApp, getActiveApp, hasMorpheusAccess, hasOnTimeAccess, normalizeAppName } from "./appAccess";

function isLoginNavigationLocked() {
  if (typeof window === 'undefined') return false;
  try { return window.sessionStorage.getItem('im.loginInProgress') === '1'; } catch (_) { return false; }
}

function clearMorpheusProfileGateState() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('mc-current-profile');
    window.localStorage.removeItem('mc-active-profile-current');
    window.sessionStorage.removeItem('mc-current-profile');
    window.sessionStorage.removeItem('mc-active-profile-current');
  } catch (_) {}
}

function resolveReturnTo(locationState) {
  const from = locationState?.from;
  const pathname = typeof from === 'string' ? from : from?.pathname;
  if (typeof pathname !== 'string') return '';
  if (!pathname.startsWith('/')) return '';
  if (pathname === '/login' || pathname === '/signup' || pathname === '/ontime-login') return '';
  return pathname + (from?.search || '') + (from?.hash || '');
}

function sanitizeReturnToForApp(returnTo, requested) {
  const target = String(returnTo || "");
  const app = normalizeAppName(requested || "neo");
  if (!target.startsWith("/")) return "";
  if (app === "mc") return target.startsWith("/morpheus-count") ? target : "";
  if (app === "ontime") return target.startsWith("/ontime") ? target : "";
  if (target.startsWith("/morpheus-count") || target.startsWith("/ontime") || target.startsWith("/ontime-login")) return "";
  return target;
}

export default function LoginGuard({ children, preferredApp = '' }) {
  const { user, claims, loading } = useAuth();
  const loc = useLocation();

  // During an explicit login attempt, keep the user on the new login handoff/splash.
  // Without this, Firebase auth can briefly expose the previous session and LoginGuard
  // auto-redirects to the old layer before LoginPage finishes routing to the selected layer.
  if (isLoginNavigationLocked()) return children;

  if (loading || (user && claims == null)) return children;
  if (!user) return children;

  if (loc.state?.accessDeniedMessage) {
    clearActiveApp();
    return children;
  }

  const returnTo = resolveReturnTo(loc.state);

  // Keep the public Sign in button honest: /login should show Login, not auto-jump into MC.
  if (loc.pathname === '/login' && !returnTo && !preferredApp && !loc.state?.preferredApp) {
    clearActiveApp();
    return children;
  }

  const requested = normalizeAppName(preferredApp || loc.state?.preferredApp || getActiveApp() || '');
  const safeReturnTo = sanitizeReturnToForApp(returnTo, requested);

  if (requested === 'ontime') {
    if (!hasOnTimeAccess(claims)) return children;
    return <Navigate to={safeReturnTo || getOnTimeLandingPath(claims)} replace />;
  }

  if (requested === 'mc') {
    if (!hasMorpheusAccess(claims)) return children;
    clearMorpheusProfileGateState();
    try { window.sessionStorage.setItem('mc-force-access-layer', '1'); } catch (_) {}
    return <Navigate to={safeReturnTo || '/morpheus-count/workspace'} replace />;
  }

  const target = safeReturnTo || getLandingPath(claims, { activeAppHint: requested || 'neo' });
  return <Navigate to={target} replace />;
}
